/**
 * Absetzung für Abnutzung (AfA): what a fixed asset costs per year (§§ 7, 8, 13 EStG).
 *
 * The year is the calendar year (an Einnahmen-Ausgaben-Rechner's Wirtschaftsjahr). Amounts are
 * whole cents. The rules, as of 2026:
 *
 *  - **Geringwertige Wirtschaftsgüter** (§ 13 EStG): costing up to 1.000 € (from 2023; 800 € in
 *    2020-2022, 400 € before) they are written off in full in the year they are put to use.
 *  - **Linear** (§ 7 Abs 1): cost spread evenly over the useful life.
 *  - **Degressiv** (§ 7 Abs 1a, since 1 July 2020): at most 30 % of the remaining book value a
 *    year. Not for cars with a combustion engine (§ 7 Abs 1a Z 2). Switching to linear is allowed
 *    once; the schedule switches in the first year linear gives more.
 *  - **Halbjahres-AfA** (§ 7 Abs 2): an asset used for no more than six months of a year gets half
 *    the year's amount - put to use after 30 June, or given up before 1 July.
 *  - **Cars** (§ 8 Abs 6 Z 1, Luxustangente): at least eight years, and no more than 40.000 € of
 *    the cost depreciated. A car's cost is gross: its input tax is not deductible.
 *  - **Private use**: only the business share is an expense; the rest is a withdrawal.
 *  - **Disposal**: the book value left on the day it goes is an expense of that year.
 *
 * Not modelled: buildings, electric cars' input-tax split, the Investitionsfreibetrag and used
 * assets' shorter lives. The person enters the useful life; the hints say what is usual.
 */

export type AfaMethod = "linear" | "degressive";
export type AssetType = "general" | "car";

export type AssetInput = {
  type: AssetType;
  /** The day it was put to use (Inbetriebnahme), ISO. */
  inServiceDate: string;
  /** Anschaffungskosten in euros: net when the input tax was deducted, gross otherwise. */
  cost: number;
  usefulLifeYears: number;
  method: AfaMethod;
  /** Percent of the remaining book value, at most 30; only for `degressive`. */
  degressiveRate?: number;
  /** 0-100: the share used privately, which is not an expense. */
  privateUsePercent?: number;
  /** ISO day it was sold, scrapped or taken private. */
  disposalDate?: string;
};

export type AfaYear = {
  year: number;
  /** The year's AfA in cents, business and private share together. */
  depreciation: number;
  /** The business share of `depreciation` plus `residual`: what the income statement deducts. */
  deductible: number;
  /** Book value written off on disposal, in cents (only in the disposal year). */
  residual: number;
  /** Book value at the end of the year (or on disposal), in cents. */
  bookValueEnd: number;
  /** Half the year's amount under § 7 Abs 2. */
  half: boolean;
  method: AfaMethod | "gwg";
};

export type AfaProblem =
  | "no_date"
  | "no_cost"
  | "life_too_short"
  | "car_life"
  | "degressive_rate"
  | "degressive_car"
  | "disposal_before_service";

export const CAR_MIN_LIFE = 8;
/** Luxustangente: the most of a car's cost that is depreciated (§ 1 PKW-Angemessenheitsverordnung). */
export const CAR_COST_CAP = 40_000;
export const MAX_DEGRESSIVE_RATE = 30;

/** The GWG limit for an asset put to use on `date`. */
export function gwgLimit(date: string): number {
  if (date >= "2023-01-01") return 1000;
  if (date >= "2020-01-01") return 800;
  return 400;
}

const cents = (euros: number) => Math.round(euros * 100);
const yearOf = (date: string) => Number(date.slice(0, 4));

/** The cost that is depreciated: a car's is capped (Luxustangente). */
export function depreciableCost(input: Pick<AssetInput, "type" | "cost">): number {
  return input.type === "car" ? Math.min(input.cost, CAR_COST_CAP) : input.cost;
}

export function isGwg(input: Pick<AssetInput, "type" | "cost" | "inServiceDate">): boolean {
  return input.type !== "car" && input.cost > 0 && input.cost <= gwgLimit(input.inServiceDate);
}

export function afaProblem(input: AssetInput): AfaProblem | null {
  if (!/^\d{4}-\d{2}-\d{2}/.test(input.inServiceDate ?? "")) return "no_date";
  if (!(input.cost > 0)) return "no_cost";
  if (isGwg(input)) return null;
  if (!(input.usefulLifeYears >= 1)) return "life_too_short";
  if (input.type === "car" && input.usefulLifeYears < CAR_MIN_LIFE) return "car_life";
  if (input.method === "degressive") {
    if (input.type === "car") return "degressive_car";
    const rate = input.degressiveRate ?? 0;
    if (!(rate > 0 && rate <= MAX_DEGRESSIVE_RATE)) return "degressive_rate";
  }
  if (input.disposalDate && input.disposalDate.slice(0, 10) < input.inServiceDate.slice(0, 10)) {
    return "disposal_before_service";
  }
  return null;
}

/**
 * The asset's AfA year by year, from the year it was put to use until it is written off or
 * disposed of. Empty when the input has a problem (`afaProblem`).
 */
export function afaSchedule(input: AssetInput): AfaYear[] {
  if (afaProblem(input)) return [];
  const start = input.inServiceDate.slice(0, 10);
  const startYear = yearOf(start);
  const disposal = input.disposalDate ? input.disposalDate.slice(0, 10) : "";
  const disposalYear = disposal ? yearOf(disposal) : Infinity;
  const privateShare = Math.min(100, Math.max(0, input.privateUsePercent ?? 0)) / 100;
  const businessPart = (amount: number) => Math.round(amount * (1 - privateShare));
  let bookValue = cents(depreciableCost(input));
  const years: AfaYear[] = [];

  const push = (year: number, depreciation: number, half: boolean, method: AfaYear["method"]) => {
    bookValue -= depreciation;
    let residual = 0;
    if (year === disposalYear) {
      residual = bookValue;
      bookValue = 0;
    }
    years.push({
      year,
      depreciation,
      deductible: businessPart(depreciation) + businessPart(residual),
      residual,
      bookValueEnd: bookValue,
      half,
      method,
    });
  };

  if (isGwg(input)) {
    push(startYear, bookValue, false, "gwg");
    return years;
  }

  const life = input.usefulLifeYears;
  // § 7 Abs 2: more than six months of use in the first year means a full year.
  const halfFirst = start.slice(5) > "06-30";
  const rate = (input.degressiveRate ?? 0) / 100;
  let method: AfaMethod = input.method;
  // Years of the useful life used up at the start of a year (a half year counts half).
  let used = 0;
  // The linear phase: its starting book value, the life left then, and the years used since.
  // Cumulative amounts are rounded, never each year's, so the cents add up to the cost exactly.
  let base = bookValue;
  let linearLife = life;
  let linearUsed = 0;

  for (let year = startYear; bookValue > 0 && year <= disposalYear; year += 1) {
    const halfDisposal = year === disposalYear && disposal.slice(5) < "07-01";
    const half = (year === startYear && halfFirst) || halfDisposal;
    const share = half ? 0.5 : 1;

    // Degressive until a linear spread of what is left over the life left gives more.
    if (method === "degressive" && year > startYear && life - used <= 1 / rate) {
      method = "linear";
      base = bookValue;
      linearLife = Math.max(life - used, share);
      linearUsed = 0;
    }

    let amount: number;
    if (method === "degressive") {
      amount = Math.round(bookValue * rate * share);
    } else {
      const upTo = (usedYears: number) => Math.round(base * Math.min(1, usedYears / linearLife));
      amount = upTo(linearUsed + share) - upTo(linearUsed);
      linearUsed += share;
    }
    push(year, Math.min(bookValue, amount), half, method);
    used += share;
  }
  return years;
}

/** The book value at the end of `year` (the cost before the asset was put to use). */
export function bookValueAt(input: AssetInput, year: number): number {
  const schedule = afaSchedule(input);
  let value = cents(depreciableCost(input));
  for (const row of schedule) {
    if (row.year > year) break;
    value = row.bookValueEnd;
  }
  return value;
}
